import React from 'react';
import { Printer, X, AlertTriangle } from 'lucide-react';
import { formatClinicDate, formatClinicTime } from '../utils/clinicTime';
import { formatPatientAge } from '../utils/age';
import { PAYMENT_MODE_LABEL as SHARED_PAYMENT_MODE_LABEL } from '../constants/payments';
import { printHtml, URDU_PRINT_FONT } from '../utils/printDocument';
import { guardianRelLabel } from '../constants/guardians';
import { accompaniedByOf } from '../utils/guardians';
import useDialog from '../hooks/useDialog';

/**
 * The 80mm thermal token receipt, shared by the two places that produce one:
 *   - Patient Management → Issue Token (the original slip, printed right after the visit is created)
 *   - Token Queue → Reprint (a duplicate for a patient who lost or never received theirs)
 *
 * Keeping the markup, the print stylesheet and the Visit → receipt mapping in one module means a
 * reprint is guaranteed to be identical to the original; there is no second copy to drift.
 */

// Display labels for the Visit.PaymentMode enum. Taken from the shared map rather than kept as a
// third copy: this one had gone stale and was missing Bank Transfer and the wallets entirely, so a
// token paid that way printed the raw enum name on the patient's slip.
export const PAYMENT_MODE_LABEL = {
  ...SHARED_PAYMENT_MODE_LABEL,
  // The one deliberate difference. On a token slip NONE means the consultation was free, not that
  // somebody forgot to record how it was paid.
  NONE: 'No charge',
};

/**
 * Render a visit's token with its per-doctor prefix (e.g. "AB-1"). Tokens are sequenced
 * per-doctor-per-day, so the prefix is what makes one doctor's "1" distinct from another's. The
 * prefix is snapshotted onto the visit at issuance (visit.tokenPrefix); fall back to the live
 * doctor record, then to a bare number for legacy prefix-less visits.
 *
 * `fallback` is what to show when there is no token number at all: the queue badge wants a visible
 * "?" placeholder, the receipt wants an empty string.
 */
/**
 * When a visit's token was issued, as a value the clinic-time helpers can format. Prefers the
 * audited createdAt (full date + time); falls back to the visit date and time combined, then to
 * the date alone for rows that predate the timestamp.
 *
 * Lives here with formatVisitToken because it is the same kind of thing: reading one fact off a
 * visit. Three screens show the issue time and they must agree on where it comes from.
 */
export const visitIssuedAt = (v) => {
  if (!v) return null;
  if (v.createdAt) return v.createdAt;
  if (v.visitDate && v.visitTime) return `${v.visitDate}T${v.visitTime}`;
  return v.visitDate || null;
};

export const formatVisitToken = (visit, { fallback = '' } = {}) => {
  if (!visit || visit.tokenNumber === null || visit.tokenNumber === undefined) {
    return visit?.tokenNumber ?? fallback;
  }
  const num = String(visit.tokenNumber).replace(/[^0-9]/g, '') || visit.tokenNumber;
  const prefix = visit.tokenPrefix || visit.doctor?.tokenPrefix;
  return prefix ? `${prefix}-${num}` : num;
};

/**
 * Map a Visit (as returned by /api/visits, which serialises the entity whole) into the shape the
 * receipt renders. Used by the reprint path; the issue-token path builds the same shape from the
 * create response plus the form's selected doctor/service, since those aren't echoed back.
 */
export const buildTokenPrintData = (visit, { waitingCount = 0 } = {}) => {
  if (!visit) return null;
  return {
    tokenNumber: visit.tokenNumber ?? 'N/A',
    tokenDisplay: formatVisitToken(visit) || 'N/A',
    patient: visit.patient,
    doctor: visit.doctor,
    service: visit.medicalService || visit.service,
    originalFee: Number(visit.originalFee ?? visit.finalFee ?? 0),
    discountAmount: Number(visit.discountAmount ?? 0),
    finalFee: Number(visit.finalFee ?? 0),
    paymentMode: visit.paymentMode,
    isEmergency: !!visit.isEmergency,
    // Who brought a child in, as the visit recorded it.
    accompaniedBy: accompaniedByOf(visit),
    // Prefer the audit createdAt (full date+time); fall back to combining the visit's date and
    // time columns into a single parseable value, then to the bare date.
    visitDate:
      visit.createdAt ||
      (visit.visitDate && visit.visitTime ? `${visit.visitDate}T${visit.visitTime}` : visit.visitDate),
    visitId: visit.id,
    waitingCount,
  };
};

// The thermal-printer stylesheet for the slip. The app's own Tailwind sheet is not loaded in the
// print window, so everything the printed slip needs is here.
const RECEIPT_STYLES = `
          @page {
            size: 80mm auto;
            margin: 0;
          }
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: 'Courier New', monospace;
            font-size: 12px;
            width: 80mm;
            padding: 5mm;
            background: white;
          }
          .receipt {
            width: 100%;
          }
          .header {
            text-align: center;
            border-bottom: 1px dashed #000;
            padding-bottom: 8px;
            margin-bottom: 8px;
          }
          .clinic-name {
            font-size: 16px;
            font-weight: bold;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          .clinic-subtitle {
            font-size: 14px;
            font-weight: bold;
            text-transform: uppercase;
          }
          .info-section {
            margin: 8px 0;
            padding: 8px 0;
            border-bottom: 1px dashed #000;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            margin: 3px 0;
          }
          .info-label {
            font-weight: bold;
          }
          .token-section {
            text-align: center;
            padding: 15px 0;
            border-bottom: 1px dashed #000;
          }
          .token-label {
            font-size: 12px;
            margin-bottom: 5px;
          }
          .token-number {
            font-size: 48px;
            font-weight: bold;
            line-height: 1;
          }
          .token-timings {
            font-size: 11px;
            margin-top: 6px;
            line-height: 1.35;
          }
          .waiting-info {
            text-align: center;
            padding: 10px 0;
            font-weight: bold;
          }
          .footer {
            text-align: center;
            margin-top: 10px;
            font-size: 11px;
          }
          .footer-line {
            margin: 3px 0;
          }
          .emergency-badge {
            background: #000;
            color: #fff;
            padding: 3px 8px;
            font-weight: bold;
            text-align: center;
            margin: 5px 0;
          }
          /* The Urdu note, in the face and weight the on-screen preview shows. With no rule here it
             inherited Courier New from the body and printed in that font's plain Urdu letters. */
          .urdu-text {
            font-family: ${URDU_PRINT_FONT};
            font-weight: 600;
            line-height: 1.625;
          }
`;

/**
 * Print a receipt DOM node: its innerHTML under the thermal-printer stylesheet above, in a small
 * window of its own. printHtml loads the Urdu face into that window and waits for it before
 * printing (the slip used to print the moment the window loaded, before a web font could arrive),
 * then closes the window.
 *
 * Returns false when the browser blocked the popup, so the caller can tell the user rather than
 * failing silently.
 */
export const printTokenReceipt = (node) => {
  if (!node) return false;
  return printHtml({
    title: 'Token Receipt',
    styles: RECEIPT_STYLES,
    bodyHtml: node.innerHTML,
    features: 'width=300,height=600',
  });
};

const formatDate = (date) => formatClinicDate(date, { day: '2-digit', month: 'short', year: 'numeric' });
const formatTime = (date) => formatClinicTime(date, { hour: '2-digit', minute: '2-digit', hour12: true });

/**
 * Receipt preview + print/dismiss actions.
 *
 * `isReprint` only changes the on-screen wording, swapping the header and dismiss button away from
 * the "just generated" phrasing. The printed slip is deliberately identical to the original: a
 * patient who lost theirs should end up holding the same thing everyone else has.
 */
const TokenPrintModal = ({
  show,
  printData,
  printRef,
  onPrint,
  onClose,
  isReprint = false,
  title,
  subtitle,
  cancelLabel,
}) => {
  const printDialogRef = useDialog(onClose, { confirmDiscard: false });
  if (!show || !printData) return null;

  const heading = title || (isReprint ? 'Reprint Token' : 'Token Generated!');
  const subheading = subtitle || (isReprint ? 'Print a duplicate slip' : 'Print receipt for patient');
  const dismissLabel = cancelLabel || (isReprint ? 'Close' : 'Skip Print');

  return (
    <div ref={printDialogRef} className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Printer className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{heading}</h2>
                <p className="text-emerald-100 text-sm">{subheading}</p>
              </div>
            </div>
            <button
              aria-label="Close"
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-xl transition-colors"
            >
              <X className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>

        {/* Receipt Preview */}
        <div className="p-6">
          <div className="bg-gray-50 rounded-2xl p-4 border-2 border-dashed border-gray-200">
            {/* Printable Receipt Content */}
            <div ref={printRef} className="receipt">
              {/* Clinic Header */}
              <div className="header text-center border-b border-dashed border-gray-400 pb-3 mb-3">
                <div className="clinic-name text-lg font-bold tracking-wide">PERVEEN ASLAM</div>
                <div className="clinic-subtitle text-base font-bold">MEMORIAL HOSPITAL</div>
              </div>

              {/* Patient Info */}
              <div className="info-section border-b border-dashed border-gray-400 pb-3 mb-3 text-sm">
                {/* Printed token receipts are physical paper — kept to non-sensitive ID only.
                    Father/Husband/Guardian, CNIC and mobile are deliberately omitted to avoid
                    leaving PII on a slip that could be photographed, dropped, or shared. The one
                    addition is who brought a child in, and only as a relationship ("Mother"):
                    the doctor learns who is giving the history, and no name goes on the paper. */}
                <div className="info-row flex justify-between mb-1">
                  <span className="font-semibold">Patient Name:</span>
                  <span>{printData.patient?.firstName} {printData.patient?.lastName}</span>
                </div>
                <div className="info-row flex justify-between mb-1">
                  <span className="font-semibold">MR#:</span>
                  <span>{printData.patient?.mrn || 'N/A'}</span>
                </div>
                <div className="info-row flex justify-between mb-1">
                  <span className="font-semibold">Age / Gender:</span>
                  <span>
                    {formatPatientAge(printData.patient) || '—'}
                    {' / '}
                    {printData.patient?.gender || 'N/A'}
                  </span>
                </div>
                {printData.accompaniedBy && (
                  <div className="info-row flex justify-between mb-1">
                    <span className="font-semibold">Brought by:</span>
                    <span>
                      {printData.accompaniedBy.relationship
                        ? guardianRelLabel(printData.accompaniedBy.relationship, printData.accompaniedBy.relationshipOther)
                        : 'Guardian'}
                    </span>
                  </div>
                )}
              </div>

              {/* Visit Details */}
              <div className="info-section border-b border-dashed border-gray-400 pb-3 mb-3 text-sm">
                <div className="info-row flex justify-between mb-1">
                  <span className="font-semibold">Doctor:</span>
                  <span>{printData.doctor?.firstName} {printData.doctor?.lastName}</span>
                </div>
                <div className="info-row flex justify-between mb-1">
                  <span className="font-semibold">Service:</span>
                  <span>{printData.service?.name || 'Consultation'}</span>
                </div>
                <div className="info-row flex justify-between mb-1">
                  <span className="font-semibold">Fee:</span>
                  <span>PKR {printData.originalFee?.toLocaleString() || 0}</span>
                </div>
                {printData.discountAmount > 0 && (
                  <div className="info-row flex justify-between mb-1 text-emerald-600">
                    <span className="font-semibold">Discount:</span>
                    <span>PKR {printData.discountAmount?.toLocaleString() || 0}</span>
                  </div>
                )}
                <div className="info-row flex justify-between mb-1 font-bold">
                  <span>Paid:</span>
                  <span>
                    PKR {printData.finalFee?.toLocaleString() || 0}
                    {printData.paymentMode && printData.paymentMode !== 'NONE' && (
                      <> ({PAYMENT_MODE_LABEL[printData.paymentMode] || printData.paymentMode})</>
                    )}
                  </span>
                </div>
              </div>

              {/* Emergency Badge */}
              {printData.isEmergency && (
                <div className="emergency-badge flex items-center justify-center gap-1.5 bg-red-600 text-white text-center py-1 px-2 font-bold text-sm rounded mb-3">
                  <AlertTriangle className="h-4 w-4" />
                  <span>EMERGENCY TOKEN</span>
                </div>
              )}

              {/* Token Number */}
              <div className="token-section text-center py-4">
                <div className="token-label text-sm text-gray-600">Your Token Number is:</div>
                <div className="token-number text-6xl font-bold text-gray-900 my-2">
                  {printData.tokenDisplay || printData.tokenNumber}
                </div>
                {printData.doctor?.roomNumber && (
                  <div className="token-room text-lg font-bold text-gray-800 mt-1">
                    Room {printData.doctor.roomNumber}
                  </div>
                )}
                {/* The doctor's clinic timings, so the patient knows when to come back: one line per
                    run of days, in English only (written by the server, util/DoctorTimings). */}
                {printData.doctor?.workingHoursText && (
                  <div className="token-timings text-xs text-gray-700 mt-1.5">
                    {printData.doctor.workingHoursText.split(' · ').map((line) => (
                      <div key={line}>{line}</div>
                    ))}
                  </div>
                )}
              </div>

              {/* Waiting Info */}
              <div className="waiting-info text-center border-t border-dashed border-gray-400 pt-3 mt-3">
                <div className="font-bold text-sm">
                  Total {printData.waitingCount || 0} Patient{printData.waitingCount !== 1 ? 's' : ''} waiting.
                </div>
                <div className="text-sm text-gray-600 mt-1">Wait for your turn. Thanks</div>
                <div className="text-xs text-gray-500 mt-2">
                  {formatDate(printData.visitDate)} {formatTime(printData.visitDate)}
                </div>
              </div>

              {/* Footer */}
              <div className="footer text-center mt-4 pt-3 border-t border-dashed border-gray-400">
                <div className="text-xs text-gray-600">
                  Printed by: {(JSON.parse(localStorage.getItem('clinic_auth_user') || '{}')?.fullName) || 'Reception'}
                </div>
                <div
                  className="urdu-text text-xs text-gray-700 mt-2 leading-relaxed"
                  dir="rtl"
                  style={{ direction: 'rtl', fontSize: '11px' }}
                >
                  یہ ٹوکن قبول کرنے کا مطلب ہے کہ آپ ہسپتال میں معائنہ اور علاج کروانے کی مکمل ذمہ داری خود لے رہے ہیں۔
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="px-6 pb-6 flex space-x-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 border-2 border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
          >
            {dismissLabel}
          </button>
          <button
            onClick={onPrint}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-xl font-bold hover:from-emerald-700 hover:to-emerald-800 transition-all flex items-center justify-center space-x-2 shadow-lg"
          >
            <Printer className="h-5 w-5" />
            <span>Print Token</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TokenPrintModal;
